import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crossword } from "../components/Crossword";
import { ShoeGame } from "../components/ShoeGame";
import { ApiError, api } from "../lib/api";
import { useSession } from "../lib/session";

type Match = { id: string; name: string; party: string };
type SavedResponse = {
  friday: number | null;
  saturday: number | null;
  dietary: string | null;
};
type PartyData = {
  party: string;
  members: { id: string; name: string }[];
  responses: Record<string, SavedResponse>;
};

// The games sit behind a name gate: you tell us who you are, and we only let you
// in once your party has actually RSVP'd. A party counts as RSVP'd the moment
// any member has a Friday or Saturday answer on file.
type Stage =
  | { kind: "search" }
  | { kind: "blocked"; name: string }
  | { kind: "choosing"; name: string; guestId: string }
  | { kind: "crossword"; name: string; guestId: string }
  | { kind: "shoegame"; name: string; guestId: string };

export function Games() {
  const [stage, setStage] = useState<Stage>({ kind: "search" });

  return (
    <div className="mx-auto max-w-4xl">
      {(stage.kind === "search" || stage.kind === "choosing") && (
        <header className="py-8 text-center">
          <h1 className="font-serif text-5xl text-pine md:text-6xl">Games</h1>
          <p className="mx-auto mt-4 max-w-xl font-sans text-lg font-normal tracking-[0.02em] text-pine">
            We love games! We've made two for you to test how well you know us,
            and totally not to incentivize you to fill out the{" "}
            <Link
              to="/rsvp"
              className="underline decoration-pine/40 underline-offset-4 transition-colors hover:text-midnight-teal hover:decoration-midnight-teal"
            >
              RSVP
            </Link>
            . 😇
          </p>
        </header>
      )}

      {stage.kind === "search" && <NameGate onResolved={setStage} />}

      {stage.kind === "blocked" && (
        <RsvpWall
          name={stage.name}
          onBack={() => setStage({ kind: "search" })}
        />
      )}

      {stage.kind === "choosing" && (
        <GamePicker
          onCrossword={() =>
            setStage({
              kind: "crossword",
              name: stage.name,
              guestId: stage.guestId,
            })
          }
          onShoeGame={() =>
            setStage({
              kind: "shoegame",
              name: stage.name,
              guestId: stage.guestId,
            })
          }
        />
      )}

      {stage.kind === "crossword" && (
        <section className="-mt-8">
          <BackToGames
            onBack={() =>
              setStage({
                kind: "choosing",
                name: stage.name,
                guestId: stage.guestId,
              })
            }
          />
          <Crossword guestId={stage.guestId} />
        </section>
      )}

      {stage.kind === "shoegame" && (
        <section className="-mt-8">
          <BackToGames
            onBack={() =>
              setStage({
                kind: "choosing",
                name: stage.name,
                guestId: stage.guestId,
              })
            }
          />
          <ShoeGame guestId={stage.guestId} />
        </section>
      )}
    </div>
  );
}

/** "← All games" link shown above an open game. */
function BackToGames({ onBack }: { onBack: () => void }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="mb-6 text-sm uppercase tracking-[0.2em] text-midnight-teal transition-colors hover:text-pine"
    >
      ← All games
    </button>
  );
}

/** Two-up chooser: pick a game. On desktop the halves are split by a hairline
 *  and a single diamond (the site's rule vocabulary); on mobile they stack with
 *  the same rule turned horizontal. Tiles tint and nudge their arrow on hover. */
function GamePicker({
  onCrossword,
  onShoeGame,
}: {
  onCrossword: () => void;
  onShoeGame: () => void;
}) {
  return (
    <div className="mx-auto mt-10 max-w-3xl">
      <div className="relative mt-8 grid md:grid-cols-2">
        {/* Vertical center rule with a diamond — desktop only. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-6 left-1/2 hidden -translate-x-1/2 flex-col items-center md:flex"
        >
          <span className="w-px flex-1 bg-pine/15" />
          <span className="my-3 inline-block h-1.5 w-1.5 rotate-45 bg-sage-moss" />
          <span className="w-px flex-1 bg-pine/15" />
        </div>

        <GameTile
          title="K or B?"
          blurb="Test how well you know us..."
          onClick={onShoeGame}
        />

        {/* Horizontal rule between the tiles — mobile only. */}
        <div
          aria-hidden="true"
          className="my-4 flex items-center gap-3 md:hidden"
        >
          <span className="h-px flex-1 bg-pine/15" />
          <span className="inline-block h-1.5 w-1.5 rotate-45 bg-sage-moss" />
          <span className="h-px flex-1 bg-pine/15" />
        </div>

        <GameTile
          title="The Mini Crossword"
          blurb="Who will have the fastest time?"
          onClick={onCrossword}
        />
      </div>
    </div>
  );
}

/** One selectable game half. `comingSoon` renders a muted, non-interactive
 *  placeholder for the game we haven't built yet. */
function GameTile({
  title,
  blurb,
  note,
  onClick,
  comingSoon,
}: {
  title: string;
  blurb?: string;
  note?: string;
  onClick?: () => void;
  comingSoon?: boolean;
}) {
  if (comingSoon) {
    return (
      <div className="px-8 py-14 text-center">
        <h3 className="font-serif text-3xl text-pine/40 md:text-4xl">
          {title}
        </h3>
        {note && (
          <p className="mx-auto mt-3 max-w-xs font-sans text-base italic text-pine/40">
            {note}
          </p>
        )}
        <span className="mt-5 inline-block font-sans text-sm uppercase tracking-[0.2em] text-pine/35">
          Coming soon
        </span>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="group block w-full px-8 py-14 text-center transition-colors hover:bg-pine/[0.04]"
    >
      <h3 className="font-serif text-3xl text-pine transition-colors group-hover:text-midnight-teal md:text-4xl">
        {title}
      </h3>
      {blurb && (
        <p className="mx-auto mt-3 max-w-xs font-sans text-base text-pine/70">
          {blurb}
        </p>
      )}
      <span className="mt-5 inline-block font-sans text-sm uppercase tracking-[0.2em] text-midnight-teal">
        Play{" "}
        <span className="inline-block transition-transform group-hover:translate-x-1">
          →
        </span>
      </span>
    </button>
  );
}

/** Name box + live match list, mirroring the RSVP search. Picking a match pulls
 *  the party and decides whether to let you through or send you to RSVP. */
function NameGate({ onResolved }: { onResolved: (stage: Stage) => void }) {
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState<Match[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const { refresh } = useSession();

  // Debounced name search against the CSV-backed guest list.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setMatches([]);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const r = await api.get<{ matches: Match[] }>(
          `/rsvp/search?q=${encodeURIComponent(q)}`,
        );
        setMatches(r.matches);
        setSearchError(null);
      } catch (e) {
        setMatches([]);
        if (e instanceof ApiError && e.status === 401) {
          void refresh();
        } else {
          setSearchError("Something went wrong - try again in a second.");
        }
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [query, refresh]);

  const choose = async (match: Match) => {
    setChecking(true);
    // Log that this guest looked themselves up — a "who's seen the site" signal.
    void api.post("/rsvp/seen", { guestId: match.id }).catch(() => {});
    try {
      const data = await api.get<PartyData>(
        `/rsvp/party?guest=${encodeURIComponent(match.id)}`,
      );
      const rsvped = Object.values(data.responses).some(
        (r) => r.friday !== null || r.saturday !== null,
      );
      const name = match.name.split(" ")[0] ?? "";
      onResolved(
        rsvped
          ? { kind: "choosing", name, guestId: match.id }
          : { kind: "blocked", name },
      );
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        void refresh();
      } else {
        setSearchError("We couldn't look you up — please try again.");
      }
      setChecking(false);
    }
  };

  return (
    <div className="mx-auto max-w-md">
      <p className="text-center font-sans text-base text-pine/70">
        Who's playing?
      </p>

      <form
        className="mt-6"
        onSubmit={(e) => {
          e.preventDefault();
          const first = matches[0];
          if (first) void choose(first);
        }}
      >
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={checking}
          placeholder="type your first or last name…"
          className="w-full border-b border-pine/25 bg-transparent pb-2 text-center font-serif text-2xl text-pine outline-none transition placeholder:italic placeholder:text-pine/35 focus:border-midnight-teal disabled:opacity-50"
        />
      </form>

      {query.trim().length >= 2 && (
        <div className="mt-2">
          {searchError ? (
            <p className="mt-6 text-center italic text-dusty-rose">
              {searchError}
            </p>
          ) : matches.length === 0 ? (
            <p className="mt-6 text-center italic text-pine/55">
              No match for &ldquo;{query.trim()}&rdquo; — try your first or last
              name.
            </p>
          ) : (
            <ul className="divide-y divide-pine/10 border-b border-pine/10">
              {matches.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    disabled={checking}
                    onClick={() => void choose(m)}
                    className="w-full py-4 text-center font-serif text-2xl text-pine transition-colors hover:text-midnight-teal disabled:opacity-50"
                  >
                    {m.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/** Shown when the guest's party hasn't RSVP'd yet — a playful stop sign with a
 *  way through to the RSVP page. */
function RsvpWall({ name, onBack }: { name: string; onBack: () => void }) {
  return (
    <div className="mx-auto max-w-md pb-10 pt-2 text-center">
      <span aria-hidden="true" className="font-serif text-3xl text-dusty-rose">
        ❃
      </span>
      <h2 className="mt-4 font-serif text-4xl text-pine">Not so fast…</h2>
      <p className="mx-auto mt-4 max-w-sm font-sans text-lg font-normal tracking-[0.02em] text-pine">
        You haven't RSVP'd yet!
      </p>
      <Link
        to="/rsvp"
        className="mt-8 inline-block bg-midnight-teal px-10 py-3 font-sans text-base font-medium uppercase tracking-[0.2em] text-cream transition-colors hover:bg-pine"
      >
        Fine...
      </Link>
      <div></div>
    </div>
  );
}
